/**
 * WorkspaceMembersCacheInvalidation
 *
 * Mounts the member-related RPC event listeners that keep the service cache
 * (`.tasks/service-cache/plan.md`) fresh. It renders its children unchanged.
 *
 * Mounted exactly once, app-wide (in `Chat2Provider`), so each listener runs
 * once per tab — a hook each consumer mounted would multiply subscriptions.
 *
 * Two events are whole-scope on purpose (decision 9):
 * `AGENT_UPDATED` carries an `Agent` (no `workspaceId`, `agentTypes.ts`) and
 * `PROFILE_UPDATED` carries no member info at all, so neither can identify a
 * single workspace on the wire. Both are rare, and the per-key debounce plus
 * stale-while-revalidate keep the cost tiny — a burst coalesces to one refetch
 * and cached rosters keep rendering meanwhile.
 */

import type { FC, ReactNode } from 'react';
import { RPC_EVENT_AGENT_UPDATED_IDENT, type AgentUpdatedEvent } from '@bloomstack/apis/agentTypes';
import { RPC_EVENT_PROFILE_UPDATED_IDENT, type ProfileUpdatedEvent } from '@bloomstack/apis/modelTypes';
import {
  RPC_EVENT_MEMBER_ADDED_IDENT,
  RPC_EVENT_MEMBER_REMOVED_IDENT,
  type MemberAddedEvent,
  type MemberRemovedEvent,
} from '@bloomstack/apis/workspace';
import { useServiceEventListener } from '@/hooks/useService';
import { useUser } from '@/hooks/useUser';
import { cacheInvalidate, cacheInvalidatePrefix } from '@/db/serviceCacheDatabase';
import { MEMBERS_KEY_PREFIX, membersKey } from './useWorkspaceMembers';

export const WorkspaceMembersCacheInvalidation: FC<{ children: ReactNode }> = ({ children }) => {
  const [user] = useUser();
  const userId = user?.id;

  // Member added → that workspace's roster changed.
  useServiceEventListener<MemberAddedEvent>(
    RPC_EVENT_MEMBER_ADDED_IDENT,
    (member) => {
      if (!userId) return;
      cacheInvalidate(userId, membersKey(member.workspaceId));
    },
    [userId],
  );

  // Member removed → that workspace's roster changed.
  useServiceEventListener<MemberRemovedEvent>(
    RPC_EVENT_MEMBER_REMOVED_IDENT,
    (workspaceId) => {
      if (!userId) return;
      cacheInvalidate(userId, membersKey(workspaceId));
    },
    [userId],
  );

  // Agent updated → payload has no workspaceId; invalidate every member key.
  useServiceEventListener<AgentUpdatedEvent>(
    RPC_EVENT_AGENT_UPDATED_IDENT,
    () => {
      if (!userId) return;
      cacheInvalidatePrefix(userId, MEMBERS_KEY_PREFIX);
    },
    [userId],
  );

  // Profile updated → payload has no member info; invalidate every member key.
  useServiceEventListener<ProfileUpdatedEvent>(
    RPC_EVENT_PROFILE_UPDATED_IDENT,
    () => {
      if (!userId) return;
      cacheInvalidatePrefix(userId, MEMBERS_KEY_PREFIX);
    },
    [userId],
  );

  return <>{children}</>;
};
